import { Injectable } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { downloadData, uploadData } from 'aws-amplify/storage';

/**
 * The S3 key hero-api issued for an image. It is used exactly as given; the web app never builds object paths.
 */
export function resolveObjectKey(image: { id?: string; s3ObjectKey?: string }): string {
  const key = image?.s3ObjectKey;
  if (typeof key !== 'string' || !key.trim()) {
    throw new Error(
      `Cannot access S3 for image ${image?.id}: missing s3ObjectKey from the API`
    );
  }
  return key.trim();
}

/**
 * Direct S3 access for hero report images using federated credentials from the Cognito
 * Identity Pool (through AWS Amplify Storage). No pre-signed URLs are requested from or
 * issued by hero-api.
 */
@Injectable({
  providedIn: 'root',
})
export class HeroReportImageStorageService {
  // Object URLs already created for images, by S3 key, so an image is only downloaded once per page load.
  private objectUrls = new Map<string, Promise<string>>();

  /**
   * Fails early with a clear message when the signed-in user can't be exchanged for AWS
   * credentials, instead of surfacing a generic S3 403.
   */
  private async assertFederatedCredentials(): Promise<void> {
    const { credentials } = await fetchAuthSession();
    if (!credentials?.accessKeyId) {
      throw new Error(
        'No federated AWS credentials available. The user must be signed in and the Cognito identity pool must be configured.'
      );
    }
  }

  /**
   * Uploads an image to S3 at the key hero-api issued for it.
   */
  async upload(image: { id?: string; s3ObjectKey?: string }, blob: Blob): Promise<void> {
    await this.assertFederatedCredentials();
    const path = resolveObjectKey(image);

    await uploadData({
      path,
      data: blob,
      options: { contentType: blob.type || 'application/octet-stream' },
    }).result;
  }

  /**
   * A browser URL (blob:) for showing an image, downloaded from S3 with federated credentials.
   * Resolves to undefined, and logs, when the image can't be downloaded, so one bad image
   * doesn't break the page that shows it.
   */
  async getObjectUrl(image: { id?: string; s3ObjectKey?: string }): Promise<string | undefined> {
    try {
      const path = resolveObjectKey(image);

      let url = this.objectUrls.get(path);
      if (!url) {
        url = this.download(path);
        this.objectUrls.set(path, url);
        // Don't cache failures; a later attempt (for example after sign-in) may work.
        url.catch(() => this.objectUrls.delete(path));
      }
      return await url;
    } catch (error) {
      console.error('Unable to load report image', image?.id, error);
      return undefined;
    }
  }

  private async download(path: string): Promise<string> {
    await this.assertFederatedCredentials();
    const { body } = await downloadData({ path }).result;
    return URL.createObjectURL(await body.blob());
  }
}
